import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Bot, 
  Target, 
  LineChart, 
  Shield, 
  History,
  Wallet,
  Layers,
  Users,
  UserCircle,
  Plug,
  PieChart,
  Settings,
  Gem,
  Zap,
  BarChart3,
  Briefcase,
  ShieldCheck
} from 'lucide-react';

const adminNavItems = [
  { 
    path: '/dashboard', 
    icon: LayoutDashboard, 
    label: 'Overview',
    description: 'System status & metrics',
    exact: true
  },
  { 
    path: '/dashboard/portfolio', 
    icon: PieChart, 
    label: 'Portfolio',
    description: 'Aggregated positions & PnL'
  },
  { 
    path: '/dashboard/admin', 
    icon: Users, 
    label: 'Admin Panel',
    description: 'Manage clients & licenses'
  },
  { 
    path: '/dashboard/defi-exchanges', 
    icon: Layers, 
    label: 'DeFi Exchanges',
    description: 'Token selection & DEXs'
  },
  { 
    path: '/dashboard/exchange-login', 
    icon: Plug, 
    label: 'Exchange Connect',
    description: 'Wallet & exchange login'
  },
  { 
    path: '/dashboard/volume-bot', 
    icon: Bot, 
    label: 'Volume Bot',
    description: 'Uptime trading automation'
  },
  { 
    path: '/dashboard/lure-bot', 
    icon: Target, 
    label: 'Lure/Sniper',
    description: 'Micro-buy triggers'
  },
  { 
    path: '/dashboard/token-safety', 
    icon: ShieldCheck, 
    label: 'Token Safety',
    description: 'Pre-trade rug & risk radar'
  },
  { 
    path: '/dashboard/bot-earnings', 
    icon: Wallet, 
    label: 'Bot Earnings',
    description: 'Profits & ZK transfer'
  },
  { 
    path: '/dashboard/analytics', 
    icon: LineChart, 
    label: 'Analytics',
    description: 'Market data & charts'
  },
  { 
    path: '/dashboard/zk-vault', 
    icon: Shield, 
    label: 'ZK Vault',
    description: 'Privacy mixer'
  },
  { 
    path: '/dashboard/history', 
    icon: History, 
    label: 'History',
    description: 'Transaction logs'
  },
  { 
    path: '/dashboard/staking', 
    icon: Gem, 
    label: 'Staking Vault',
    description: 'Stake GLabs, earn bonus'
  },
  { 
    path: '/dashboard/admin-config', 
    icon: Settings, 
    label: 'Config',
    description: 'Environment & endpoints'
  },
  { 
    path: '/dashboard/god-mode', 
    icon: Zap, 
    label: 'God-Mode',
    description: 'Agency & tier CRUD'
  },
  { 
    path: '/dashboard/affiliate-sla', 
    icon: BarChart3, 
    label: 'Affiliate SLA',
    description: 'Agency performance'
  },
  { 
    path: '/dashboard/glabs', 
    icon: Zap, 
    label: 'GLabs Tracker',
    description: 'Supply, sales & price control'
  },
];

const subUserNavItems = [
  { 
    path: '/dashboard/client', 
    icon: UserCircle, 
    label: 'My Dashboard',
    description: 'Bots, earnings & withdrawals'
  },
  { 
    path: '/dashboard/portfolio', 
    icon: PieChart, 
    label: 'Portfolio',
    description: 'Aggregated positions & PnL'
  },
  { 
    path: '/dashboard/exchange-login', 
    icon: Plug, 
    label: 'Exchange Connect',
    description: 'Wallet & exchange login'
  },
  { 
    path: '/dashboard/volume-bot', 
    icon: Bot, 
    label: 'Volume Bot',
    description: 'Uptime trading automation'
  },
  { 
    path: '/dashboard/lure-bot', 
    icon: Target, 
    label: 'Lure/Sniper',
    description: 'Micro-buy triggers'
  },
  { 
    path: '/dashboard/staking', 
    icon: Gem, 
    label: 'Staking Vault',
    description: 'Stake GLabs, earn bonus'
  },
];

export function Sidebar() {
  const location = useLocation();
  const { isAdmin, isSubUser } = useAuth();

  const navItems = isSubUser ? subUserNavItems : adminNavItems;

  return (
    <aside className="w-64 min-h-screen bg-[hsl(220,18%,11%)] border-r border-[hsl(220,16%,18%)] p-4 flex flex-col" data-testid="sidebar">
      <div className="mb-6 px-2">
        <h2 className="text-lg font-bold font-['Outfit'] text-white">
          GauriLabs <span className="text-[#E8520F]">Commander</span>
        </h2>
        <p className="text-xs text-[hsl(215,16%,45%)] mt-0.5">
          {isSubUser ? 'Client Portal' : 'Admin Control Center'}
        </p>
      </div>

      <nav className="space-y-1 flex-1">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.exact}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
                isActive
                  ? 'bg-[#E8520F]/10 text-[#E8520F] font-medium'
                  : 'text-[hsl(215,16%,56%)] hover:bg-[hsl(220,16%,14%)] hover:text-white'
              }`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
          >
            <item.icon className="w-4.5 h-4.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="truncate">{item.label}</p>
              <p className="text-[10px] text-[hsl(215,16%,40%)] truncate">{item.description}</p>
            </div>
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto pt-4 border-t border-[hsl(220,16%,18%)] px-2">
        <p className="text-[10px] text-[hsl(215,16%,40%)]">
          {isSubUser ? 'Sub-User Account' : 'Super Admin'}
        </p>
        <p className="text-xs text-[hsl(215,16%,56%)] font-mono mt-0.5 truncate">
          v3.0 — 3-6-9 Engine
        </p>
      </div>
    </aside>
  );
}
